import React from 'react'
import {
    CaretDownFilled,
    CaretUpFilled,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    Loading3QuartersOutlined,
    ReloadOutlined,
    SelectOutlined,
} from '@ant-design/icons'

import { BetterComponent } from '../../../components/better-component/better-component'
import { AntdTableEmptyPlaceholder, Button } from '@pushly/aqe/lib/components'

import { AppService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { titleCase } from '../../../_utils/utils'
import { AppState } from '../../../stores/app'
import { observe } from 'mobx'
import { PromptService } from '../../../services/prompt'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { NotificationAbBadge } from '../../../components/badges/notification-ab-badge'
import { StatusType } from '../../../enums/status-type'
import { StatusBadge } from '@pushly/aqe/lib/components'
import { CurrentUserCan } from '../../../stores/app-ability'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { NoTranslate } from '../../../components/no-translate/no-translate'
type MenuItem = Required<MenuProps>['items'][number]
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { PromptStyle } from '@pushly/models/lib/enums'
import { GetPromptGroupResponse } from '@pushly/models/lib/structs/prompts/get-prompt-group-response'
import { GetAbstractPromptResponse } from '@pushly/models/lib/structs'
import { Alert, Button as AntDButton, Flex, MenuProps, Modal, Popover, Space, Table, Tooltip, Typography } from 'antd'

import './prompt-table-well.scss'

const PriorityHandler = ({ promptGroup, onChange, hideUp, hideDown }: any) => {
    return (
        <Space size="medium" vertical className="priority-handler">
            {!hideUp && (
                <span
                    onClick={() => {
                        onChange(promptGroup, -1)
                    }}
                >
                    <CaretUpFilled />
                </span>
            )}
            {!hideDown && (
                <span
                    onClick={() => {
                        onChange(promptGroup, 1)
                    }}
                >
                    <CaretDownFilled />
                </span>
            )}
        </Space>
    )
}

// TODO: Add filter to table for channels

interface IProps {
    readonly showSearch?: boolean
    readonly showRefresh?: boolean
    readonly showHeader?: boolean
    readonly title?: string
    readonly actions?: React.ReactNode
    readonly showActions?: boolean
    readonly pageSize?: number
    readonly listType?: DeliveryChannel
}

interface IState {
    loading: boolean
    currentDomainId: number
    promptsLoaded: boolean
    allPromptGroups: GetPromptGroupResponse[]
    visiblePromptGroups: GetPromptGroupResponse[]
    sortedInfo: any
    pageMeta: any
    page: number
    pageSize: number
    query?: string
}

export class PromptTableWell extends BetterComponent<IProps, IState> {
    private appState: AppState
    private appService: AppService
    private promptService: PromptService

    private disposeObservers: any

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.promptService = Container.get(PromptService)

        this.state = {
            page: 1,
            pageSize: this.props.pageSize || 15,
            pageMeta: {},
            loading: false,
            currentDomainId: this.appState.currentDomain!.id,
            promptsLoaded: false,
            allPromptGroups: [],
            visiblePromptGroups: [],
            sortedInfo: {
                order: 'descend',
                columnKey: 'id',
            },
        }
    }

    public componentDidMount(): void {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.ensurePromptsForList())]

        this.ensurePromptsForList()
    }

    public componentWillUnmount(): void {
        this.disposeObservers.forEach((f: any) => f())
    }

    public render(): React.ReactNode {
        const wellProps: any = {
            hideFooter: true,
            hideHeader: true,
            title: 'Prompts',
        }

        const actions = (
            <React.Fragment>
                {this.props.actions}

                {this.props.showRefresh !== false && (
                    <Tooltip title="Refresh">
                        <AntDButton
                            className="refresh-list"
                            type="text"
                            shape="round"
                            icon={<ReloadOutlined className="text--size--large" spin={this.state.loading} />}
                            onClick={this.refreshNotifications}
                        />
                    </Tooltip>
                )}
            </React.Fragment>
        )

        if (this.props.showHeader !== false) {
            wellProps.showHeader = true
            wellProps.action = actions

            if (this.props.title && this.props.title.trim()) {
                wellProps.title = this.props.title
            }
        }

        const wellClassNames: string[] = ['type-2', 'prompt-table-well']
        if (this.props.showHeader !== false) {
            wellClassNames.push('show-header')
        }
        if (this.props.showSearch === false) {
            wellClassNames.push('hide-search')
        }
        wellProps.className = wellClassNames.join(' ')

        const getRowKey = (record: any) => `${record.id}||${record.isActive}`
        const abTestIds = this.state.visiblePromptGroups.filter((g) => g.isAbTest).map(getRowKey)

        let show2StepWarning = false
        const currentDomain = this.appState.currentDomain
        if (!this.state.loading && currentDomain) {
            const match = this.state.allPromptGroups
                // Must have an active 2-step prompt
                .filter((pg) => pg.isActive)
                // Must have at least one non-system prompt variant
                .filter(
                    (pg) => pg.prompts.length >= 1 && pg.prompts.filter((p) => p.style !== PromptStyle.NATIVE).length,
                )

            if (match.length === 0 && this.props.listType === DeliveryChannel.WEB) {
                show2StepWarning = true
            }
        }

        const domainOwnedPromptIdentity = this.appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.PROMPT)

        return (
            <Table
                className="prompt-table aqe-table-well"
                classNames={{
                    body: { row: 'prompt-table-row' },
                }}
                title={
                    show2StepWarning
                        ? () => (
                              <Alert
                                  className="safari-2step"
                                  type="warning"
                                  showIcon={true}
                                  title="2-step Prompt Requirements"
                                  description={
                                      <span>
                                          You must have at least one Slide prompt configured and enabled in order to
                                          prompt visitors using Safari 12.1 or later and Firefox 72 or later.
                                      </span>
                                  }
                              />
                          )
                        : undefined
                }
                rowKey={getRowKey}
                loading={
                    this.state.loading && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Prompts" icon={<SelectOutlined />} />,
                }}
                dataSource={this.state.visiblePromptGroups}
                onChange={this.handleChange}
                pagination={false}
                bordered={true}
                expandable={{
                    expandedRowClassName: 'prompt-table-expanded-row',
                    expandRowByClick: true,
                    expandedRowKeys: abTestIds,
                    showExpandColumn: false,
                    rowExpandable: (record: GetPromptGroupResponse) => record.isAbTest,
                    expandedRowRender: (group: GetPromptGroupResponse, ...args: any[]): React.ReactNode => {
                        if (!group.isAbTest) {
                            return undefined
                        }

                        return (
                            <Table
                                rowKey="id"
                                dataSource={group.prompts}
                                pagination={false}
                                bordered={true}
                                showHeader={false}
                            >
                                <Table.Column key="prompt" className="prompt" render={this.renderNestedDisplay} />
                                <CurrentUserCan do={AbilityAction.UPDATE} on={group}>
                                    <Table.Column
                                        key="actions"
                                        className="actions"
                                        render={this.renderNestedRowActions}
                                        align="center"
                                        width={120}
                                    />
                                </CurrentUserCan>
                            </Table>
                        )
                    },
                }}
            >
                {this.props.listType === DeliveryChannel.WEB && (
                    <Table.Column
                        key="priority"
                        className="priority"
                        dataIndex="priority"
                        title={
                            <span>
                                Priority
                                <Popover
                                    getPopupContainer={this.appService.getAppContainer}
                                    classNames={{
                                        content: 'sw-popover pr-priority-header-popover',
                                    }}
                                    content={
                                        <Typography.Paragraph>
                                            Prompt priority determines which prompt will be shown to a potential
                                            subscriber. When a potential subscriber visits a page on your site, we will
                                            determine the prompt with the highest priority (lowest number) that matches
                                            all page conditions for that page and display that prompt.
                                        </Typography.Paragraph>
                                    }
                                    trigger="hover"
                                >
                                    <ExclamationCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        }
                        render={this.renderPriority}
                        width={120}
                        align="center"
                    />
                )}
                <Table.Column
                    key="prompt"
                    className={`prompt${this.props.listType === DeliveryChannel.WEB ? '' : ' native'}`}
                    title="Prompt"
                    render={this.renderDisplay}
                />

                {/*
                    Antd Table.Column elements are required to be first class children
                    <CurrentUserCan ... /> cannot be used in this context
                */}
                {this.appState.abilityStore.can(AbilityAction.UPDATE, domainOwnedPromptIdentity) && (
                    <Table.Column
                        key="actions"
                        className="actions"
                        title="Actions"
                        render={this.renderRowActions}
                        align="center"
                        width={120}
                    />
                )}
            </Table>
        )
    }

    private handleRowMove = async (dataSource: any[]) => {
        await this.setState({ loading: true })

        const priorityMap = dataSource.map((p, priority) => ({ id: p.id, priority }))
        await this.promptService.updatePromptGroupPriorityMap(this.state.currentDomainId, priorityMap)

        dataSource.forEach((ds) => {
            const mapData = priorityMap.find((m) => m.id === ds.id)
            if (mapData) ds.priority = mapData.priority
        })

        this.setState({
            loading: false,
            visiblePromptGroups: dataSource,
        })
    }

    private handlePriorityChange = async (group: GetPromptGroupResponse, modifier: 1 | -1) => {
        const dataSource = [...this.state.visiblePromptGroups]
        const currIdx = dataSource.findIndex((pg) => pg.id.toString() === group.id.toString())

        if (currIdx !== -1) {
            const elem = dataSource.splice(currIdx, 1)[0]
            dataSource.splice(currIdx + modifier, 0, elem)
        }

        return this.handleRowMove(dataSource)
    }

    private renderPriority = (_: string, group: GetPromptGroupResponse, idx: number): React.ReactNode => {
        const { abilityStore } = this.appState
        const dragEnabled = this.state.visiblePromptGroups.length > 1
        const isFirstRow = idx === 0
        const isLastRow = idx === this.state.visiblePromptGroups.length - 1

        return (
            <Flex gap={16} align="center" justify="flex-end" style={{ width: '50%' }}>
                <CurrentUserCan
                    do={AbilityAction.UPDATE}
                    on={abilityStore.getDomainOwnedIdentityFor(SubjectEntity.PROMPT)}
                >
                    {!dragEnabled ? (
                        ''
                    ) : (
                        <PriorityHandler
                            promptGroup={group}
                            onChange={this.handlePriorityChange}
                            hideUp={isFirstRow}
                            hideDown={isLastRow}
                        />
                    )}
                </CurrentUserCan>
                {(group.priority || 0) + 1}
            </Flex>
        )
    }

    private renderDisplay = (_: string, group: GetPromptGroupResponse): React.ReactNode => {
        const title = group.name
        let promotedPromptName: string | null | undefined

        const prompt: GetAbstractPromptResponse = group.prompts[0] || {}
        if (group.prompts.length === 1 && prompt.computedName !== title) {
            promotedPromptName = prompt.name
        }

        const pageConditions = group.conditions?.page
        const visitorConditions = prompt.conditions?.visitor

        let pageConditionDisplay = `Shows on all pages`
        if (pageConditions) {
            const hasPageUrlCondition = !!pageConditions.pageUrls || !!pageConditions.excludedPageUrls
            pageConditionDisplay = ''

            if (pageConditions.pageUrls && pageConditions.pageUrls.length > 0) {
                pageConditionDisplay += `<Typography.Paragraph className="text--size--small">Shows on pages matching the following url patterns: ${pageConditions.pageUrls.join(
                    ', ',
                )}.</Typography.Paragraph>`
            }
            if (pageConditions.excludedPageUrls && pageConditions.excludedPageUrls.length > 0) {
                pageConditionDisplay += `<Typography.Paragraph className="text--size--small">Does not show on pages matching the following url patterns: ${pageConditions.excludedPageUrls.join(
                    ', ',
                )}.</Typography.Paragraph>`
            }

            if (!hasPageUrlCondition && pageConditions.keywords) {
                pageConditionDisplay = `<Typography.Paragraph className="text--size--small">Shows on pages containing the following meta keywords: ${pageConditions.keywords.join(
                    ', ',
                )}.</Typography.Paragraph>`
            }
        }

        let visitorConditionDisplay: React.ReactNode = `Shows immediately`
        if (visitorConditions) {
            if (visitorConditions.timeOnPageSeconds !== undefined) {
                const value = visitorConditions.timeOnPageSeconds
                visitorConditionDisplay = `Shows to users who have been on a single page for at least ${value} seconds in the current session.`
            } else if (visitorConditions.sessionPageViews !== undefined) {
                const value = visitorConditions.sessionPageViews
                visitorConditionDisplay = `Shows to users who have been visited at least ${value} pages on your site in the current session.`
            } else if (visitorConditions.sessionTimeOnSiteSeconds !== undefined) {
                const value = visitorConditions.sessionTimeOnSiteSeconds
                visitorConditionDisplay = `Shows to users who have been on your site for at least ${value} seconds in the current session.`
            } else if (visitorConditions.scrollDepthPct !== undefined && visitorConditions.scrollDepthPct !== null) {
                const value = visitorConditions.scrollDepthPct
                visitorConditionDisplay = (
                    <span>
                        Scroll Depth: {value}%{" "}
                        <Tooltip title={<>Shows to users who have scrolled down at least {value}% of the page.</>}>
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </span>
                )
            }
        }

        const configurationDetails: any = (
            <div>
                <h2>Page Conditions</h2>
                <span dangerouslySetInnerHTML={{ __html: pageConditionDisplay }} />

                {!group.isAbTest && (
                    <React.Fragment>
                        <hr />
                        <h2>Visitor Conditions</h2>
                        {visitorConditionDisplay}
                    </React.Fragment>
                )}

                <hr />
                <h2>Show prompt automatically?</h2>
                {group.prompts[0].isAutoShow
                    ? 'Yes'
                    : 'This prompt will only be shown when the action is specifically triggered via the Platform SDK.'}
            </div>
        )

        const configurationDetailsHandle = (
            <Popover
                getPopupContainer={() => this.appService.getAppContainer()}
                classNames={{
                    root: 'prompt-row-details-popover',
                    content: 'sw-popover',
                }}
                content={configurationDetails}
                trigger="hover"
            >
                <span className="prompt-row-details-popover-trigger">
                    <Typography.Text className="prompt-row-config-details-slug">Configuration Details</Typography.Text>
                </span>
            </Popover>
        )

        return (
            <TableRowEntityDisplay
                title={
                    <NoTranslate>
                        {`${title}${promotedPromptName ? ` (Variant: ${promotedPromptName})` : ''}`}
                    </NoTranslate>
                }
                description={
                    this.props.listType === DeliveryChannel.WEB ? (
                        <Space>
                            <span className="info-icon">
                                <SelectOutlined />
                            </span>
                            {configurationDetailsHandle}
                        </Space>
                    ) : null
                }
                status={
                    <StatusBadge
                        type="prompt"
                        status={group.isActive ? StatusType.ACTIVE.name : StatusType.DISABLED.name}
                        expanded={true}
                    />
                }
                badges={
                    group.prompts.length > 1 && (
                        <>
                            <NotificationAbBadge tooltip="Prompt Test" />
                            {!!promotedPromptName && (
                                <span className="promoted-name">
                                    <b>Promoted prompt:</b> {promotedPromptName}
                                </span>
                            )}
                        </>
                    )
                }
            />
        )
    }

    private renderNestedDisplay = (_: string, source: any, rowIndex: number): React.ReactNode => {
        const prompt: GetAbstractPromptResponse = source
        const testName = prompt.name || `Test ${rowIndex + 1}`

        let style: PromptStyle | 'CUSTOM' = prompt.style

        const visitorConditions: any = prompt.conditions?.visitor
        const isPublisherCustomStyle = style === PromptStyle.PUBLISHER_CUSTOM

        if (isPublisherCustomStyle) {
            style = 'CUSTOM'
        }

        let visitorConditionDisplay: React.ReactNode = `Shows immediately`
        if (visitorConditions) {
            if (visitorConditions.timeOnPageSeconds !== undefined) {
                const value = visitorConditions.timeOnPageSeconds
                visitorConditionDisplay = `Shows to users who have been on a single page for at least ${value} seconds in the current session.`
            } else if (visitorConditions.sessionPageViews !== undefined) {
                const value = visitorConditions.sessionPageViews
                visitorConditionDisplay = `Shows to users who have been visited at least ${value} pages on your site in the current session.`
            } else if (visitorConditions.sessionTimeOnSiteSeconds !== undefined) {
                const value = visitorConditions.sessionTimeOnSiteSeconds
                visitorConditionDisplay = `Shows to users who have been on your site for at least ${value} seconds in the current session.`
            } else if (visitorConditions.scrollDepthPct !== undefined && visitorConditions.scrollDepthPct !== null) {
                const value = visitorConditions.scrollDepthPct
                visitorConditionDisplay = (
                    <span>
                        Scroll Depth: {value}%{" "}
                        <Tooltip title={<>Shows to users who have scrolled down at least {value}% of the page.</>}>
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </span>
                )
            }
        }

        const configurationDetails: any = (
            <div>
                <h2>Visitor Conditions</h2>
                <Typography.Paragraph>{visitorConditionDisplay}</Typography.Paragraph>
            </div>
        )

        const configurationDetailsHandle = (
            <Popover
                getPopupContainer={() => this.appService.getAppContainer()}
                classNames={{
                    content: 'prompt-row-details-popover',
                }}
                content={configurationDetails}
                trigger="hover"
            >
                <span className="prompt-row-details-popover-trigger">
                    <Typography.Text className="prompt-row-config-details-slug">Configuration Details</Typography.Text>
                </span>
            </Popover>
        )

        return (
            <TableRowEntityDisplay
                title={<NoTranslate>{testName}</NoTranslate>}
                description={
                    <Space>
                        <span className="info-icon">
                            <SelectOutlined />
                        </span>
                        <span className="pd-style-type">
                            <strong>{titleCase(style)}</strong>:
                        </span>
                        {configurationDetailsHandle}
                    </Space>
                }
                status={
                    <span className="test-weight">
                        <span>{prompt.weight}%</span>
                    </span>
                }
                badges={<NotificationAbBadge tooltip="Prompt Test" />}
            />
        )
    }

    private renderRowActions = (group: GetPromptGroupResponse): React.ReactNode => {
        const actions: any = [
            {
                text: 'Duplicate',
                icon: 'copy',
                onClick: this.handleDuplicate,
                group,
                altHref: this.buildDuplicateUrl(group),
            },
        ]

        if (group.isActive) {
            actions.push({
                text: 'Disable',
                icon: 'close',
                onClick: this.handleDisable,
                group,
            })
        } else {
            actions.push({
                text: 'Enable',
                icon: 'eye',
                onClick: this.handleEnable,
                group,
            })
        }

        if (this.state.visiblePromptGroups.length > 1) {
            actions.push({ divider: true })
            actions.push({
                text: 'Delete',
                icon: 'delete',
                onClick: this.handleDelete,
                group,
            })
        }

        if (this.props.showActions === false) {
            return ''
        }

        return this.props.listType === DeliveryChannel.WEB ? (
            <Button
                actions={actions}
                onClick={() => this.handlePrimaryActionClick(group)}
                size="small"
                altHref={this.buildEditUrl(group)}
            >
                <span>Edit</span>
            </Button>
        ) : (
            <Button
                onClick={() => (group.isActive ? this.handleDisable(group) : this.handleEnable(group))}
                size="small"
            >
                <span>{group.isActive ? 'Disable' : 'Enable'}</span>
            </Button>
        )
    }

    private renderNestedRowActions = (source: any): React.ReactNode => {
        const prompt: GetAbstractPromptResponse = source
        const group = this.state.visiblePromptGroups.find((g) => g.id === prompt.promptGroupId)
        const domainOwnedPromptIdentity = this.appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.PROMPT)

        if (!group) {
            return null
        }

        return (
            this.appState.abilityStore.can(AbilityAction.UPDATE, domainOwnedPromptIdentity) && (
                <Button
                    onClick={() => this.handlePromptTestPromotion(prompt, group)}
                    size="small"
                    confirm={{
                        okText: 'Promote',
                        title: 'Promote Prompt',
                        content: (
                            <div>
                                <p>Promoting this prompt will end the "{group.name}" test.</p>
                                <p>
                                    The "{prompt.name}" prompt will be promoted to 100% display weight and the test will
                                    no longer be visible in the prompt list. Historical test reporting will be available
                                    in the reporting module.
                                </p>
                                <p>Please note, this action cannot be reversed.</p>
                            </div>
                        ),
                    }}
                >
                    <span>Promote</span>
                </Button>
            )
        )
    }

    private buildEditUrl(group: GetPromptGroupResponse): string {
        return this.appService.routeWithinDomain(`/prompts/${group.id}`, true)
    }

    private buildDuplicateUrl(group: GetPromptGroupResponse): string {
        return this.appService.routeWithinDomain(`/prompts/new?template=${group.id}`, true)
    }

    private handleChange = (pagination: any, filters: any, sorter: any) => {
        this.setState({
            sortedInfo: sorter,
        })
    }

    private handlePrimaryActionClick(prompt: GetPromptGroupResponse): void {
        this.appService.route(this.buildEditUrl(prompt))
    }

    private async handlePromptTestPromotion(
        prompt: GetAbstractPromptResponse,
        group: GetPromptGroupResponse,
    ): Promise<void> {
        const promoted = await this.promptService.promoteTestPrompt(group, prompt)

        if (promoted) {
            this.fetchPrompts()
        }
    }

    private handleDuplicate = async (item: any) => {
        const { group } = item.props
        this.appService.route(this.buildDuplicateUrl(group))
    }

    private handleEnable = async (item: MenuItem | GetPromptGroupResponse): Promise<void> => {
        if (!item) return

        let group
        if ('props' in item) {
            group = (item.props as any).group
        } else {
            group = item
        }

        try {
            await this.promptService.enablePromptGroupById(this.state.currentDomainId, group.id)
        } catch (error) {
            simpleNotification('error', error.message)
        }

        group.isActive = true

        const visiblePrompts = this.state.visiblePromptGroups
        const visibleNotifIdx = visiblePrompts.findIndex((p) => p.id === group.id)
        if (visibleNotifIdx >= 0) {
            visiblePrompts.splice(visibleNotifIdx, 1, group)
            this.setState({ visiblePromptGroups: visiblePrompts })
            simpleNotification(
                'success',
                <span>
                    Prompt <b>{group.name}</b> has been enabled.
                </span>,
            )
        }
    }

    private handleDisable = async (item: MenuItem | GetPromptGroupResponse): Promise<void> => {
        if (!item) return

        let group
        if ('props' in item) {
            group = (item.props as any).group
        } else {
            group = item
        }

        const finalize = async () => {
            try {
                await this.promptService.disablePromptGroupById(this.state.currentDomainId, group.id)
            } catch (error) {
                simpleNotification('error', error.message)
            }

            group.isActive = false

            const visiblePrompts = this.state.visiblePromptGroups
            const visibleNotifIdx = visiblePrompts.findIndex((p) => p.id === group.id)
            if (visibleNotifIdx >= 0) {
                visiblePrompts.splice(visibleNotifIdx, 1, group)
                this.setState({ visiblePromptGroups: visiblePrompts })
                simpleNotification(
                    'success',
                    <span>
                        Prompt <b>{group.name}</b> has been disabled.
                    </span>,
                )
            }
        }

        const allPrompts = this.state.allPromptGroups || []
        const promptIs2Step = !group.isAbTest && group.prompts[0].style !== PromptStyle.NATIVE
        const enabled2StepPrompts = allPrompts.filter(
            (g) => !g.isAbTest && g.isActive && g.prompts[0].style !== PromptStyle.NATIVE,
        )

        if (promptIs2Step && enabled2StepPrompts.length <= 1) {
            return new Promise((res) => {
                Modal.confirm({
                    title: 'Disable Prompt',
                    content: (
                        <div>
                            If you disable the only active 2-step prompt, users on Firefox and Safari will no longer be
                            prompted for notification permission. Are you sure you want to do this?
                        </div>
                    ),
                    okText: 'Yes, Disable',
                    cancelText: 'Cancel',
                    onOk: () => res(finalize()),
                    onCancel: () => res(),
                })
            })
        } else {
            return finalize()
        }
    }

    private handleDelete = async (item: any): Promise<void> => {
        const { group } = item.props

        const finalize = async () => {
            await this.promptService.destroyPromptGroupById(this.state.currentDomainId, group.id, true)
            return this.fetchPrompts()
        }

        const allPrompts = this.state.allPromptGroups || []
        const promptIs2Step = !group.isAbTest && group.isActive && group.prompts[0].style !== PromptStyle.NATIVE
        const enabled2StepPrompts = allPrompts.filter(
            (g) => !g.isAbTest && g.isActive && g.prompts[0].style !== PromptStyle.NATIVE,
        )

        if (promptIs2Step && enabled2StepPrompts.length <= 1) {
            return new Promise((res) => {
                Modal.confirm({
                    title: 'Delete Prompt',
                    content: (
                        <div>
                            If you remove the only active 2-step prompt, users on Firefox and Safari will no longer be
                            prompted for notification permission. Are you sure you want to do this?
                        </div>
                    ),
                    okText: 'Yes, Delete',
                    cancelText: 'Cancel',
                    onOk: () => res(finalize()),
                    onCancel: () => res(),
                })
            })
        } else {
            return new Promise((res) => {
                Modal.confirm({
                    title: 'Delete Prompt',
                    content: 'Are you sure you want to remove this prompt?',
                    okText: 'Delete',
                    onOk: () => res(finalize()),
                    onCancel: () => res(),
                })
            })
        }
    }

    private async ensurePromptsForList() {
        if (this.state.promptsLoaded && this.state.currentDomainId === this.appState.currentDomain!.id) {
            return
        }

        await this.setState({ loading: true, promptsLoaded: false })
        await this.fetchPrompts()
        await this.setState({ loading: false, promptsLoaded: true })
    }

    private refreshNotifications = async () => {
        this.fetchPrompts()
    }

    private async fetchPrompts(search?: any) {
        await this.setState({ loading: true })

        try {
            const res = await this.promptService.fetchPromptGroupsByDomainId(this.appState.currentDomain!.id, {
                query: {
                    pagination: 0,
                    search: search ? search : this.state.query,
                    channels: this.props.listType,
                },
            })

            if (res.ok) {
                this.setState({
                    currentDomainId: this.appState.currentDomain!.id,
                    allPromptGroups: res.data ?? [],
                    visiblePromptGroups: res.data ?? [],
                    pageMeta: res.meta,
                })
            }
        } catch (error) {
            console.log(error)
        }

        await this.setState({ loading: false })
    }
}
